/**
 * File metadata envelope for preserving filename across encode/decode.
 *
 * Wraps file content with a metadata prefix before encryption so the
 * original filename is recoverable on decode. Backwards compatible:
 * payloads without the FMET magic are treated as raw bytes.
 *
 * Envelope format:
 *   4 bytes  Magic: "FMET"
 *   2 bytes  Filename length (uint16 BE)
 *   N bytes  Filename (UTF-8)
 *   ...      File content
 */

const FMET_MAGIC = new Uint8Array([0x46, 0x4d, 0x45, 0x54]); // "FMET"

export interface FileEnvelope {
  filename: string;
  content: Uint8Array;
}

/** Wrap file content with filename metadata. */
export function wrapFileEnvelope(
  filename: string,
  content: Uint8Array
): Uint8Array {
  const nameBytes = new TextEncoder().encode(filename);
  if (nameBytes.length > 0xffff) {
    throw new Error("Filename too long (max 65535 bytes UTF-8)");
  }

  const result = new Uint8Array(4 + 2 + nameBytes.length + content.length);
  result.set(FMET_MAGIC, 0);
  result[4] = (nameBytes.length >> 8) & 0xff;
  result[5] = nameBytes.length & 0xff;
  result.set(nameBytes, 6);
  result.set(content, 6 + nameBytes.length);
  return result;
}

/** Unwrap file envelope if present. Returns null for non-FMET payloads. */
export function unwrapFileEnvelope(
  payload: Uint8Array
): FileEnvelope | null {
  if (payload.length < 6) return null;
  if (
    payload[0] !== 0x46 ||
    payload[1] !== 0x4d ||
    payload[2] !== 0x45 ||
    payload[3] !== 0x54
  ) {
    return null;
  }

  const nameLen = (payload[4]! << 8) | payload[5]!;
  if (payload.length < 6 + nameLen) return null;

  const filename = new TextDecoder().decode(payload.slice(6, 6 + nameLen));
  const content = payload.slice(6 + nameLen);
  return { filename, content };
}
